import { memo } from 'react'

import { MailOutline, SupportAgent } from '@mui/icons-material'
import { Box, Chip, Divider, Paper, Stack, Typography } from '@mui/material'
import { SmepEvent } from '@nativewaves/nw-cloud-sdk/smep'
import { useTranslation } from 'react-i18next'

import { PhoneLink } from 'domains/Workspaces/Events/Event/Dashboard/OnCall/PhoneLink'
import { onCallPersonLabel } from 'domains/Workspaces/Events/Event/onCall'

export type PanelProps = {
  event: SmepEvent
}

/**
 * Read-only by design: on call is edited through the event's Edit button, alongside the configuration,
 * so there is one way in and one Save. Here it is a read-out of who gets called, in what order.
 */
const PanelRaw = ({ event }: PanelProps) => {
  const { t } = useTranslation(['common', 'domain'])

  return (
    <Paper sx={{ px: 4, py: 3 }} elevation={0}>
      <Box sx={{ mb: 1 }}>
        <Typography variant="heading-md">{t('domain:Workspace.Event.OnCall.title')}</Typography>
        <Typography sx={{ color: 'text.secondary' }} variant="text-sm">
          {t('domain:Workspace.Event.OnCall.description')}
        </Typography>
      </Box>

      {!event.onCall.length ? (
        <Stack alignItems="center" spacing={1} sx={{ py: 4, color: 'text.secondary' }}>
          <SupportAgent />
          <Typography variant="heading-base">{t('domain:Workspace.Event.OnCall.nobodyOnCall')}</Typography>
        </Stack>
      ) : (
        <Stack divider={<Divider flexItem />}>
          {event.onCall.map((person, index) => (
            <Stack key={person.userId} direction="row" spacing={1.5} alignItems="center" sx={{ py: 1 }}>
              <Chip size="small" label={index + 1} />

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="text" noWrap>
                  {onCallPersonLabel(person)}
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ color: 'text.secondary' }}>
                  {!!person.email && (
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                      <MailOutline sx={{ fontSize: 14 }} />
                      <Typography variant="text-sm" noWrap>
                        {person.email}
                      </Typography>
                    </Stack>
                  )}
                  {!!person.phoneNumber && <PhoneLink phoneNumber={person.phoneNumber} />}
                  {!person.email && !person.phoneNumber && (
                    <Typography variant="text-sm">{t('domain:Workspace.Event.OnCall.noContactDetails')}</Typography>
                  )}
                </Stack>
              </Box>
            </Stack>
          ))}
        </Stack>
      )}
    </Paper>
  )
}

export const Panel = memo(PanelRaw)
